<template>
  <!-- Wallet Connection Modal -->
  <ConnectWalletWebThreeModal
    v-model="showConnectionModal"
    :requested-permission-mask="requestedPermissionMask"
    :loading="loading"
    :requested-token="requestedToken"
    @connect-wallet="onConnectWallet"
  />

  <!-- Token Top-Up Flow -->
  <TopUpWalletWebThreeModal
    v-model="showTopUpModal"
    :requested-token="requestedToken"
    @trigger-purchase="onTriggerPurchase"
    @disconnect="showDisconnectModal = true"
  />

  <!-- Disconnect Wallet Confirmation -->
  <DangerConfirmationModal
    v-model="showDisconnectModal"
    :title="$t('channel.auth.webThree.disconnectWalletConfirmation')"
    :confirm-label="$t('channel.auth.webThree.disconnectWallet')"
    @confirm="onDisconnectConfirmed"
  >
    <p v-if="truncatedAddress" class="text-text-secondary text-sm">
      {{
        $t("channel.auth.webThree.connectedAddress", {
          address: truncatedAddress,
        })
      }}
    </p>
  </DangerConfirmationModal>

  <WalletConnectSuccessModal
    v-model="showWalletConnectSuccessModal"
    @close="onWalletConnectSuccess"
  />

  <TokenPurchaseWebThreeModal
    v-model="showTokenPurchaseModal"
    :requested-token="requestedToken"
    :top-up-balance="topUpBalance"
    @closed="showTokenPurchaseModal = false"
  />
</template>

<script setup lang="ts">
import { ref, defineAsyncComponent, computed, watch, nextTick } from "vue";
import { graphql as gql } from "@/generated";
import { useMutation } from "@urql/vue";
import { useToastStore } from "@/stores/toast.store";
import { useI18n } from "vue-i18n";
import type { AuthorizationRuleForGating } from "@/features/gating/use-gating-permissions";
import { useCapabilitiesStore } from "@/stores/capabilities.store";
import type { WebThreeTokenWithBalance } from "@/constants/web-three";
import TokenPurchaseWebThreeModal from "./wallet/token-purchase-web-three-modal.vue";
import { useMaybeWatcher } from "@/composables/use-maybe-watcher";
import { useTrackingStore } from "@/stores/tracking.store";
import {
  EventCategory,
  TrackEventEnum,
} from "@/services/tracking/tracking-types";
import WalletConnectSuccessModal from "./wallet/wallet-connect-success-modal.vue";
import { useUserStore } from "@/stores/user.store";

// Lazy load modals
const ConnectWalletWebThreeModal = defineAsyncComponent(
  () => import("./wallet/connect-wallet-web-three-modal.vue"),
);
const TopUpWalletWebThreeModal = defineAsyncComponent(
  () => import("./wallet/top-up-wallet-web-three-modal.vue"),
);
const DangerConfirmationModal = defineAsyncComponent(
  () => import("@/components/show/modals/danger-confirmation-modal.vue"),
);

const DisconnectWalletDocument = gql(/* GraphQL */ `
  mutation DisconnectWallet {
    disconnectWallet
  }
`);

const emit = defineEmits<{
  "wallet-connected": [];
  "wallet-disconnected": [];
  "tokens-acquired": [];
  "trigger-purchase": [];
}>();

// Access wallet service from capabilities store (lazy loaded)
const capabilitiesStore = useCapabilitiesStore();
const walletService = computed(() => capabilitiesStore.walletService);
const trackingService = useTrackingStore();
const userStore = useUserStore();

const { t } = useI18n();
const toastStore = useToastStore();
const { executeMutation: disconnectWalletMutation } = useMutation(
  DisconnectWalletDocument,
);

// Internal state
const showConnectionModal = ref(false);
const showTopUpModal = ref(false);
const showTokenPurchaseModal = ref(false);
const showWalletConnectSuccessModal = ref(false);
const showDisconnectModal = ref(false);
const requestedRule = ref<AuthorizationRuleForGating | null>(null);
const requestedPermissionMask = ref<number | null>(null);
const loading = ref(false);

// Computed token from rule
const requestedToken = computed(() => {
  const criterion = requestedRule.value?.web_three_token_criterion;
  if (!criterion || !walletService.value) return null;

  const token = walletService.value.channelWebThreeTokens.find(
    (t) => t.address === criterion.web_three_erc_twenty_token?.address,
  );
  if (!token) return null;

  return {
    symbol: token.symbol,
    label: token.label,
    address: token.address,
    external_partner_id: token.external_partner_id ?? null,
    amount: criterion.min_tokens,
  } as WebThreeTokenWithBalance;
});
const topUpBalance = computed(() => {
  if (!requestedToken.value) return null;

  const tokenBalance = walletService.value?.tokenBalanceMap.get(
    requestedToken.value.address,
  );

  return requestedToken.value.amount - (tokenBalance?.amount ?? 0);
});

const truncatedAddress = computed(() => {
  const address = walletService.value?.walletAddress;
  if (!address || typeof address !== "string" || address.length < 10)
    return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
});

async function onDisconnectConfirmed() {
  try {
    const result = await disconnectWalletMutation({});
    if (result.error) {
      toastStore.addToast({
        message: t("channel.auth.webThree.disconnectWalletError"),
        type: "error",
      });
      return;
    }
    await walletService.value?.disconnect();
    // Force a fresh Me refetch so wallet status (and any wallet-derived UI) is
    // not served from URQL's document cache. Server-side Me cache is also
    // invalidated by the disconnectWallet mutation (T-012), but the client
    // cache needs an explicit refetch.
    await userStore.refetchLocalUser();
    showDisconnectModal.value = false;
    emit("wallet-disconnected");
    toastStore.addToast({
      message: t("channel.auth.webThree.walletDisconnected"),
      type: "success",
    });
  } catch {
    toastStore.addToast({
      message: t("channel.auth.webThree.disconnectWalletError"),
      type: "error",
    });
  }
}

function onWalletConnectSuccess() {
  const requestedRuleValue = requestedRule.value;

  if (requestedRuleValue) {
    showTokenTopUpFlow(requestedRuleValue);
  }
}

// Public methods (exposed to parent via template ref)
function showUpgradeFlow(rule: AuthorizationRuleForGating | null) {
  requestedRule.value = rule;
  if (!walletService.value?.isConnected) {
    showConnectionModal.value = true;
  } else {
    showTokenTopUpFlow(rule);
  }
}

function showTokenTopUpFlow(rule: AuthorizationRuleForGating | null) {
  requestedRule.value = rule;
  showTopUpModal.value = true;
}

// Event handlers
function onConnectWallet() {
  if (!walletService.value) return;

  trackingService.track({
    event: TrackEventEnum.ConnectWalletTriggered,
    category: EventCategory.Identity,
    metadata: {},
  });

  loading.value = true;
  walletService.value
    .connect()
    .then(async () => {
      // Mirror disconnect: refetch Me so the wallet now shows up in the UI
      // (server-side Me cache invalidation happens inside the connectWallet
      // mutation; this refresh bypasses URQL's document cache).
      await userStore.refetchLocalUser();
      trackingService.track({
        event: TrackEventEnum.ConnectWalletSuccess,
        category: EventCategory.Identity,
        metadata: {},
      });
    })
    .finally(() => {
      loading.value = false;
      showConnectionModal.value = false;
      emit("wallet-connected");
    });
}

function onTriggerPurchase() {
  showTopUpModal.value = false;
  showTokenPurchaseModal.value = true;
}

watch(
  [
    showConnectionModal,
    showTopUpModal,
    showTokenPurchaseModal,
    showWalletConnectSuccessModal,
    showDisconnectModal,
  ],
  async ([
    showConnectionModal,
    showTopUpModal,
    showTokenPurchaseModal,
    ,
    showDisconnectModal,
  ]) => {
    // Wait for the next tick to ensure the modals are closed
    await nextTick();

    // If all modals are closed, reset the requested permission mask
    if (
      !showConnectionModal &&
      !showTopUpModal &&
      !showTokenPurchaseModal &&
      !showWalletConnectSuccessModal.value &&
      !showDisconnectModal
    ) {
      requestedRule.value = null;
      requestedPermissionMask.value = null;
    }
  },
);

useMaybeWatcher(
  () => walletService.value?.watchTokenUpdates,
  () => {
    if (showTokenPurchaseModal.value) {
      showTokenPurchaseModal.value = false;
      showTopUpModal.value = true;

      trackingService.track({
        event: TrackEventEnum.TopUpPurchaseFlowCompleted,
        category: EventCategory.Identity,
        metadata: {
          tokenSymbol: requestedToken.value?.symbol ?? "no token",
          tokenAmount: requestedToken.value?.amount ?? 0,
        },
      });
    }
  },
);

watch(
  () => walletService.value?.wasConnectedInSession,
  (val, oldVal) => {
    if (val && !oldVal) {
      showWalletConnectSuccessModal.value = true;
    }
  },
);

// Track CONNECT_WALLET_INTENT when the connect wallet modal is shown
watch(showConnectionModal, (isShown) => {
  if (isShown) {
    const walletAddress = walletService.value?.walletAddress ?? "unknown";

    userStore.trackAnalyticsAction({
      appActionName: "CONNECT_WALLET_INTENT",
      objectId: walletAddress,
      actionMetadata: {
        requestedTokenSymbol: requestedToken.value?.symbol ?? null,
        requestedTokenAmount: requestedToken.value?.amount ?? null,
      },
    });
  }
});

// Track TOP_UP_WALLET_INTENT when the top-up modal is shown
watch(showTopUpModal, (isShown) => {
  if (isShown) {
    const walletAddress = walletService.value?.walletAddress ?? "unknown";
    userStore.trackAnalyticsAction({
      appActionName: "TOP_UP_WALLET_INTENT",
      objectId: walletAddress,
      actionMetadata: {
        requestedTokenSymbol: requestedToken.value?.symbol ?? null,
        requestedTokenAmount: requestedToken.value?.amount ?? null,
        currentBalance:
          walletService.value?.tokenBalanceMap.get(
            requestedToken.value?.address ?? "",
          )?.amount ?? 0,
      },
    });
  }
});

// Expose methods to parent
defineExpose({
  showUpgradeFlow,
});
</script>
